import { Outlet, Link, useLocation } from "react-router-dom";
import EtatConnexion from "@/components/EtatConnexion";
import { useAuth } from "@/lib/hooks/useAuth";
import { useEcranEtroit } from "@/lib/hooks/useEcranEtroit";
import { logout } from "@/lib/firebase/auth";
import NewBasketAlarm from "@/components/NewBasketAlarm";
import NotificationPrompt from "@/components/NotificationPrompt";
import BoutonLangue from "@/components/BoutonLangue";
import { useLangue } from "@/lib/i18n";

export default function FournisseurLayout() {
  // Une seule copie de la page montee (01/11/2026) : voir useEcranEtroit.
  const etroit = useEcranEtroit();
  const { user } = useAuth();
  const location = useLocation();
  // `rtl` renverse le sens de lecture. Il est pose sur les conteneurs de
  // cette mise en page, pas sur la page entiere : l'administration reste en
  // francais, et elle doit rester lisible meme si quelqu'un a choisi la
  // darija depuis ce navigateur.
  const { t, rtl } = useLangue();

  const handleLogout = async () => {
    await logout();
  };

  const navItems = [
    { path: "/fournisseur/dashboard", label: "Dashboard", icon: "📊" },
    { path: "/fournisseur/history", label: "Historique", icon: "📦" },
    { path: "/fournisseur/products", label: "Produits", icon: "🛒" },
    // La comptabilite est retiree du menu : le fournisseur n'a pas a
    // consulter les comptes depuis son ecran. L'onglet a ete masque plutot
    // que supprime pour pouvoir le remettre d'une ligne le jour ou la
    // question se represente.
    // { path: "/fournisseur/accounting", label: "Comptabilité", icon: "💰" },
    { path: "/fournisseur/remboursements", label: "Remboursements", icon: "💸" },
    { path: "/fournisseur/horaires", label: "Horaires", icon: "🕐" },
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir={rtl ? "rtl" : "ltr"}>
      {/* Mobile: Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
        <div className="flex justify-around overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex-shrink-0 py-3 px-3 text-center text-xs min-h-[44px] min-w-[60px] flex flex-col items-center justify-center ${
                location.pathname === item.path
                  ? "text-primary border-t-2 border-primary"
                  : "text-gray-600"
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-[10px] mt-1">{t(item.label)}</span>
            </Link>
          ))}
          {/* La bascule de langue vit DANS la barre du bas : c'est la seule
              chose toujours visible sur un telephone. Ailleurs, quelqu'un qui
              ne lit pas le francais ne la trouverait jamais. */}
          <div className="flex-shrink-0 py-2 px-2 flex items-center justify-center">
            <BoutonLangue />
          </div>
          <button
            onClick={handleLogout}
            className="flex-shrink-0 py-3 px-3 text-center text-xs min-h-[44px] min-w-[60px] flex flex-col items-center justify-center text-red-600 hover:bg-red-50"
          >
            <span className="text-lg">🚪</span>
            <span className="text-[10px] mt-1">{t("Déco")}</span>
          </button>
        </div>
      </nav>

      {/* Desktop: Sidebar */}
      <div className="hidden md:flex">
        <aside className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <div className="p-4">
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-xl font-bold">Natura Beldi</h1>
              <BoutonLangue />
            </div>
            <p className="text-sm text-gray-600">{t("Fournisseur")}</p>
          </div>
          <nav className="mt-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-4 py-3 ${
                  location.pathname === item.path
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {t(item.label)}
              </Link>
            ))}
          </nav>
          <div className="absolute bottom-0 w-64 p-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">{user?.name}</p>
            {!etroit && <EtatConnexion />}
            <button
              onClick={handleLogout}
              className="mt-2 text-sm text-red-600 hover:text-red-800"
            >
              {t("Déconnexion")}
            </button>
          </div>
        </aside>
        <main className="flex-1 p-6 pb-20 md:pb-6">
          {!etroit && <Outlet />}
        </main>
      </div>

      {/* L'alerte se monte dans la mise en page, pas dans une page : elle
          doit sonner meme s'il a laisse l'onglet sur ses produits ou sur sa
          comptabilite. */}
      {/* SANS CE BANDEAU, AUCUNE NOTIFICATION N'ARRIVE.
          Le navigateur n'envoie rien tant que la personne n'a pas donne son
          accord, et cet accord se demande une seule fois, depuis l'ecran.
          L'admin, le support et le responsable commande l'avaient ; le
          fournisseur, non — c'est pour cela que son telephone restait muet
          tant qu'il n'ouvrait pas l'application. */}
      <NotificationPrompt />

      <NewBasketAlarm />


      {/* Mobile: Main Content */}
      <div className="md:hidden pb-20" dir={rtl ? "rtl" : "ltr"}>
        {/* En affichage etroit, la barre laterale n'existe pas : sans cette
            bande, ni le nom ni l'etat de connexion ne sont visibles. */}
        {etroit && <EtatConnexion nom={user?.name} barre />}
        <main className="p-4">
          {etroit && <Outlet />}
        </main>
      </div>
    </div>
  );
}

